/**
 * The words of a chart period control, shared by both apps: mobile's
 * PeriodControl (#277) and the webapp's TimeTravelContainer (MR-7). Raw scale
 * and timestamp in, message descriptors and strings out; each app draws them.
 */
import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';
import * as Dates from '../Dates';
import * as Time from '../Time';

type FormatMessage = (
  descriptor: MessageDescriptor,
  values?: Record<string, string>,
) => string;

export const isRollingScale = (scale: Time.Scale): boolean =>
  Dates.LAST_SCALE_CHOICES.includes(scale);

/**
 * Names for the ‹ › arrows. Action phrasings ("Previous week" = go to it),
 * not the comparison-series labels in `calendar.previous*`.
 */
export const stepMessages = (
  scale: Time.Scale,
): {previous: MessageDescriptor; next: MessageDescriptor} | undefined => {
  switch (scale) {
    case 'day':
      return {
        previous: messages.calendar.goToPreviousDay,
        next: messages.calendar.goToNextDay,
      };
    case 'week':
      return {
        previous: messages.calendar.goToPreviousWeek,
        next: messages.calendar.goToNextWeek,
      };
    case 'month':
      return {
        previous: messages.calendar.goToPreviousMonth,
        next: messages.calendar.goToNextMonth,
      };
    case 'year':
      return {
        previous: messages.calendar.goToPreviousYear,
        next: messages.calendar.goToNextYear,
      };
    default:
      return undefined;
  }
};

/** What the pill shows: "Sep 7 – 13, 2026", or "Last 7 days". */
export const visualLabel = (
  scale: Time.Scale,
  timestamp: number,
  timezone: string,
  formatMessage: FormatMessage,
): string =>
  isRollingScale(scale)
    ? formatMessage(messages.calendar[scale])
    : Dates.formatTimestampFromScale(timestamp, timezone, scale);

/** What a screen reader says: "September 7 to 13, 2026". */
export const spokenLabel = (
  scale: Time.Scale,
  timestamp: number,
  timezone: string,
  formatMessage: FormatMessage,
): string =>
  isRollingScale(scale)
    ? formatMessage(messages.calendar[scale])
    : Dates.formatTimestampFromScale(timestamp, timezone, scale, {
        style: 'spoken',
        joinSpokenRange: (start, end) =>
          formatMessage(messages.calendar.periodRangeSpoken, {start, end}),
      });
